import React, { memo, useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { TagBadge } from './TagBadge';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import type { CorkBoardTag } from '@/types/residents/corkBoard';

interface TagPickerProps {
  tags: CorkBoardTag[];
  selectedTagIds: number[];
  onToggleTag: (tagId: number) => void;
  error?: string;
}

export const TagPicker: React.FC<TagPickerProps> = memo(({
  tags,
  selectedTagIds,
  onToggleTag,
  error,
}) => {
  const { semantic } = useThemeColors();
  const selectedSet = useMemo(() => new Set(selectedTagIds), [selectedTagIds]);

  const selectedName = useMemo(() => {
    if (selectedTagIds.length === 0) return null;
    const tag = tags.find((t) => selectedSet.has(t.id));
    return tag?.name ?? null;
  }, [tags, selectedTagIds, selectedSet]);

  return (
    <View style={styles.container}>
      <View style={styles.labelRow}>
        <Text style={[styles.label, { color: semantic.text.primary }]}>Tag</Text>
        <Text style={[styles.counter, { color: selectedName ? semantic.interactive.primary : semantic.text.muted }]}>
          {selectedName ?? 'Select a tag'}
        </Text>
      </View>

      <View style={styles.tagGrid}>
        {tags.map((tag) => {
          const isSelected = selectedSet.has(tag.id);
          return (
            <TagBadge
              key={tag.id}
              tag={{ name: tag.name, color: tag.color }}
              size="medium"
              isActive={isSelected}
              onPress={() => onToggleTag(tag.id)}
            />
          );
        })}
      </View>

      {error ? <Text style={[styles.errorText, { color: semantic.interactive.danger }]}>{error}</Text> : null}
    </View>
  );
});

TagPicker.displayName = 'TagPicker';

const styles = StyleSheet.create({
  container: {
    gap: 8,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
  },
  counter: {
    fontSize: 12,
  },
  counterMax: {
    fontWeight: '600',
  },
  tagGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  errorText: {
    fontSize: 12,
    marginTop: 2,
  },
});
